import { AllIntegrations } from '@/hooks/useIntegration';
import { MASKED_PLACEHOLDER } from '@/sections/settings/constants';
import { Account, Integration } from '@/types';

import {
  NotificationField,
  NotificationViewModal,
} from './NotificationViewModal';

/**
 * Provider name mapping for chat integrations
 */
const PROVIDER_NAMES: Record<string, string> = {
  slack: 'Slack',
  teams: 'Microsoft Teams',
  gchat: 'Google Chat',
  zulip: 'Zulip',
};

interface ChatMessagingViewModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The chat account to view/edit */
  chatAccount: Account;
}

/**
 * ChatMessagingViewModal - Modal for viewing chat configuration and editing severity threshold
 *
 * Features:
 * - Displays masked placeholder for webhook URL (stored in AWS Secrets Manager)
 * - Provider-specific modal title (Slack, Teams, GChat, Zulip)
 * - Editable severity threshold dropdown
 * - Only severity can be edited (backend constraint - PUT only updates Settings)
 *
 * Note: Chat webhook URLs ARE masked (unlike email addresses) because they
 * contain authentication tokens that should not be exposed.
 *
 * Used in:
 * - NotificationSettingsCards via ChatMessagingSection
 */
export function ChatMessagingViewModal({
  open,
  onClose,
  chatAccount,
}: ChatMessagingViewModalProps) {
  // Get provider name for modal title
  const providerName =
    PROVIDER_NAMES[chatAccount?.member] ||
    AllIntegrations[chatAccount?.member as Integration]?.name ||
    'Chat';

  const fields: NotificationField[] = [
    {
      label: 'Webhook URL',
      value: MASKED_PLACEHOLDER,
      type: 'masked',
      tooltip:
        'Webhook URL is stored in AWS Secrets Manager and cannot be viewed after creation for security reasons',
      testId: 'chat-view-url',
    },
  ];

  return (
    <NotificationViewModal
      open={open}
      onClose={onClose}
      account={chatAccount}
      title={`${providerName} Configuration`}
      username={chatAccount.member}
      fields={fields}
      successMessage={`${providerName} updated`}
      errorMessage={`Failed to update ${providerName}`}
      modalTestId="chat-view-modal"
      saveButtonTestId="chat-view-save-button"
      severitySelectTestId="chat-view-severity-select"
      severitySelectName="chat-severity"
    />
  );
}
